import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetStations } from '@ordr-as/ordr-service-types/epos';
import type { KdsTaskDetail } from '@ordr-as/ordr-service-types/epos';
import { useLocation } from '../../context/LocationContext';
import { useOrdrWebSocket } from '../../context/OrdrWebSocketProvider';
import { useOrdersWebSocketStore } from '../../context/OrdersWebSocketStore';
import { useKdsTasks } from '../../hooks/useKdsTasks';
import { useKdsFlowGraph } from '../../hooks/useKdsFlowGraph';
import { useKdsTaskMutations } from '../../hooks/useKdsTaskMutations';
import { useNow } from '../../hooks/useNow';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import Spinner from '../../components/Spinner';
import {
  selectExpoOrders,
  selectItemTotals,
  selectRecentlyDone,
  selectStationCounts,
  selectStationTasks,
  sortBoardTasks,
  isOngoingStatus,
  expoSummaryLine,
  taskSummaryLine,
} from './selectors';
import { clampFocus, moveFocus } from './keyboardNav';
import KdsToolbar from './KdsToolbar';
import type { StationSelection } from './StationDropdown';
import StationBoard from './board/StationBoard';
import ExpoBoard from './board/ExpoBoard';
import SidePanel from './panels/SidePanel';
import RecentPanel, { type RecentRow } from './panels/RecentPanel';
import OrderDetailSheet from './panels/OrderDetailSheet';
import './animations.css';
import { useScreenSize } from '../../context/ScreenSizeContext';

type PanelId = 'none' | 'recent';

const scrollCardIntoView = (cardId: string) => {
  const el = document.querySelector(`[data-kds-card][data-card-id="${CSS.escape(cardId)}"]`);
  el?.scrollIntoView({ block: 'nearest' });
};

const KdsBoard = ({ expo = false }: { expo?: boolean }) => {
  const { locationId: locationIdParam, stationId: stationIdParam } = useParams<{
    locationId: string;
    stationId: string;
  }>();
  const { location } = useLocation();
  const navigate = useNavigate();
  const { isDesktop } = useScreenSize();

  const { setStations } = useOrdrWebSocket();
  const connectionState = useOrdersWebSocketStore((s) => s.connectionState);
  const { data: stations, isPending: stationsPending } = useGetStations(location.id);
  const { graph, isPending: graphPending } = useKdsFlowGraph(location.id);
  const { data: tasks, isPending: tasksPending } = useKdsTasks(location.id);
  const { bump, recall, pendingTaskIds } = useKdsTaskMutations(location.id);
  const now = useNow();

  // The board needs every station live: dropdown counts, "Waiting for X" and
  // Expo all span stations
  useEffect(() => {
    const ids = (stations ?? []).map((s) => s.id).filter((id): id is number => id !== undefined);
    setStations(ids);
    return () => setStations([]);
  }, [stations, setStations]);

  const [openPanel, setOpenPanel] = useState<PanelId>(() => (isDesktop ? 'recent' : 'none'));
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [focusedCardId, setFocusedCardId] = useState<string | null>(null);
  const recentBeforeDetail = useRef(false);

  const openDetail = (groupId: string) => {
    if (selectedGroupId === groupId) {
      closeDetail();
      return;
    }
    if (selectedGroupId === null) recentBeforeDetail.current = openPanel === 'recent';
    setOpenPanel('none');
    setSelectedGroupId(groupId);
  };

  const closeDetail = () => {
    setSelectedGroupId(null);
    if (recentBeforeDetail.current) setOpenPanel('recent');
  };

  const togglePanel = (panel: PanelId) => {
    recentBeforeDetail.current = false;
    setSelectedGroupId(null);
    setOpenPanel((current) => (current === panel ? 'none' : panel));
  };

  const allTasks = useMemo(() => tasks ?? [], [tasks]);
  const sortedStations = useMemo(
    () => [...(stations ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [stations]
  );
  const stationNameById = useMemo(
    () => new Map(sortedStations.map((s) => [s.id ?? 0, s.name ?? `Station ${s.id}`])),
    [sortedStations]
  );
  const counts = useMemo(() => selectStationCounts(allTasks, graph), [allTasks, graph]);
  const expoData = useMemo(() => selectExpoOrders(allTasks), [allTasks]);

  const stationId = expo ? undefined : Number(stationIdParam);
  const stationName = stationId !== undefined ? stationNameById.get(stationId) ?? `Station ${stationId}` : 'Expo';

  const itemTotals = useMemo(() => {
    if (expo) return selectItemTotals(allTasks);
    if (stationId === undefined) return [];
    return selectItemTotals(selectStationTasks(allTasks, stationId, graph));
  }, [expo, allTasks, stationId, graph]);

  const stationTasks = useMemo(
    () =>
      expo || stationId === undefined
        ? []
        : sortBoardTasks(selectStationTasks(allTasks, stationId, graph)),
    [expo, allTasks, stationId, graph]
  );

  const recentRows = useMemo<RecentRow[]>(() => {
    if (expo) {
      return expoData.recentlyServed.map((order) => ({
        id: order.groupId,
        orderNo: order.bookingOrderId,
        text: expoSummaryLine(order),
        at: order.updatedUtc,
        groupId: order.groupId,
      }));
    }
    if (stationId === undefined) return [];
    return selectRecentlyDone(allTasks, stationId, graph).map((task) => ({
      id: String(task.id),
      orderNo: task.bookingOrderId,
      text: taskSummaryLine(task),
      at: task.updatedUtc,
      groupId: task.groupId,
      undoTaskId: task.id,
    }));
  }, [expo, expoData.recentlyServed, allTasks, stationId, graph]);

  const onSelectStation = (selection: StationSelection) => {
    setSelectedGroupId(null);
    setFocusedCardId(null);
    if (selection.kind === 'expo') {
      navigate(`/${locationIdParam}/kds/expo`);
      return;
    }
    navigate(`/${locationIdParam}/kds/stations/${selection.stationId}/view`);
  };

  const deselectOnOutsideClick = (e: React.MouseEvent) => {
    if (selectedGroupId === null) return;
    if ((e.target as Element).closest('[data-kds-card]')) return;
    closeDetail();
  };

  // One nav entry per *card*: a station card is a single task (so two tasks of the
  // same order are focused independently), an expo card is a whole order.
  const navItems = useMemo(() => {
    if (expo) {
      return expoData.board
        .filter((order) => order.groupId)
        .map((order) => ({ cardId: order.groupId, groupId: order.groupId, action: undefined as (() => void) | undefined }));
    }
    return stationTasks
      .filter((task): task is KdsTaskDetail & { id: number; groupId: string } => task.id !== undefined && Boolean(task.groupId))
      .map((task) => {
        const actionable =
          !pendingTaskIds.has(task.id) && (task.status === 'QUEUED' || isOngoingStatus(task.status));
        return {
          cardId: String(task.id),
          groupId: task.groupId,
          action: actionable ? () => bump(task.id) : undefined,
        };
      });
  }, [expo, expoData.board, stationTasks, pendingTaskIds, bump]);

  const orderedIds = useMemo(() => navItems.map((item) => item.cardId), [navItems]);

  const focusBy = (direction: 1 | -1) => {
    const nextId = moveFocus(focusedCardId, orderedIds, direction);
    setFocusedCardId(nextId);
    if (nextId) scrollCardIntoView(nextId);
  };

  const activateFocused = () => {
    const item = navItems.find((entry) => entry.cardId === focusedCardId);
    // Focus can briefly point at a card that just left the board (e.g. key-repeat
    // after a bump). Skip — the clamp effect resyncs focus to the next card.
    if (!item) return;
    if (item.action) {
      item.action();
      return;
    }
    openDetail(item.groupId);
  };

  // After the list changes, keep the cursor on a live card: stay put if still
  // present, otherwise advance to whatever card slid into the completed card's
  // slot, so Enter → Enter walks the board hands-free.
  const prevOrderedIdsRef = useRef<string[]>([]);
  useEffect(() => {
    const next = clampFocus(focusedCardId, prevOrderedIdsRef.current, orderedIds);
    prevOrderedIdsRef.current = orderedIds;
    if (next !== focusedCardId) setFocusedCardId(next);
  }, [orderedIds, focusedCardId]);

  // Enter advances the focused card (Start/Done), falling back to its detail when there's
  // no action; Space opens (toggles) the detail, matching click behaviour.
  useKeyboardShortcuts({
    ArrowRight: () => focusBy(1),
    ArrowDown: () => focusBy(1),
    ArrowLeft: () => focusBy(-1),
    ArrowUp: () => focusBy(-1),
    Enter: activateFocused,
    ' ': () => {
      const item = navItems.find((entry) => entry.cardId === focusedCardId);
      if (item) openDetail(item.groupId);
    },
    Escape: () => {
      if (selectedGroupId !== null) {
        closeDetail();
        return;
      }
      if (openPanel === 'recent') setOpenPanel('none');
    },
    r: () => togglePanel('recent'),
  });

  if (stationsPending || graphPending || tasksPending) {
    return <Spinner className="m-auto mt-6 w-8 h-8" />;
  }

  return (
    <div className="relative flex flex-col h-full overflow-hidden bg-grey-lightest">
      <KdsToolbar
        stations={sortedStations}
        counts={counts}
        expoCount={expoData.board.length}
        selection={expo ? { kind: 'expo' } : { kind: 'station', stationId: stationId ?? -1 }}
        onSelect={onSelectStation}
        connectionState={connectionState}
        recentOpen={openPanel === 'recent' && selectedGroupId === null}
        onToggleRecent={() => togglePanel('recent')}
      />

      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-w-0 overflow-y-auto px-4 pb-5" onClick={deselectOnOutsideClick}>
          {expo ? (
            <ExpoBoard
              orders={expoData.board}
              graph={graph}
              stationNameById={stationNameById}
              nowMs={now}
              selectedGroupId={selectedGroupId}
              focusedCardId={focusedCardId}
              onOpenDetail={openDetail}
            />
          ) : (
            <StationBoard
              tasks={stationTasks}
              allTasks={allTasks}
              stationName={stationName}
              graph={graph}
              stationNameById={stationNameById}
              nowMs={now}
              selectedGroupId={selectedGroupId}
              focusedCardId={focusedCardId}
              pendingTaskIds={pendingTaskIds}
              onOpenDetail={openDetail}
              onStart={bump}
              onDone={bump}
            />
          )}
        </div>

        {selectedGroupId !== null && (
          <SidePanel docked={isDesktop} width={360} onScrimClick={closeDetail}>
            <OrderDetailSheet
              groupId={selectedGroupId}
              tasks={allTasks}
              graph={graph}
              stationNameById={stationNameById}
              onClose={closeDetail}
            />
          </SidePanel>
        )}
        {selectedGroupId === null && openPanel === 'recent' && (
          <SidePanel docked={isDesktop} width={248} overlayWidth={300} onScrimClick={() => setOpenPanel('none')}>
            <RecentPanel
              isExpo={expo}
              itemTotals={itemTotals}
              rows={recentRows}
              showClose={!isDesktop}
              onClose={() => setOpenPanel('none')}
              onUndo={recall}
              onOpenRow={openDetail}
            />
          </SidePanel>
        )}
      </div>
    </div>
  );
};

export default KdsBoard;
